import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const CartContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or get session ID for guest users
const getSessionId = () => {
  let sessionId = localStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substring(2) + Date.now();
    localStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ products: [], services: [] });
  const [loading, setLoading] = useState(false);
  const sessionId = getSessionId();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cart`, {
        params: { session_id: sessionId }
      });
      setCart(response.data || { products: [], services: [] });
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      setCart({ products: [], services: [] });
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      await axios.post(`${API_URL}/cart/add`, 
        { product_id: productId, quantity },
        { params: { session_id: sessionId } }
      );
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('Failed to add to cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen hozzáadás' 
      };
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      await axios.put(`${API_URL}/cart/update`,
        { product_id: productId, quantity },
        { params: { session_id: sessionId } }
      );
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('Failed to update cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen frissítés' 
      };
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API_URL}/cart/remove/${productId}`, {
        params: { session_id: sessionId }
      });
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen törlés' 
      };
    }
  };

  const addServiceToCart = async (serviceId, fullName, birthDate, questions = '', quantity = 1) => {
    try {
      await axios.post(`${API_URL}/cart/add-service`, 
        { 
          service_id: serviceId, 
          full_name: fullName,
          birth_date: birthDate,
          questions: questions,
          quantity 
        },
        { params: { session_id: sessionId } }
      );
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('Failed to add service to cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen hozzáadás' 
      };
    }
  };

  const removeServiceFromCart = async (index) => {
    try {
      await axios.delete(`${API_URL}/cart/remove-service/${index}`, {
        params: { session_id: sessionId }
      });
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error('Failed to remove service from cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen törlés' 
      };
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API_URL}/cart/clear`, {
        params: { session_id: sessionId }
      });
      setCart({ products: [], services: [] });
      return { success: true };
    } catch (error) {
      console.error('Failed to clear cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Sikertelen törlés' 
      };
    }
  };

  const cartTotal = 
    (cart.products?.reduce((sum, item) => sum + item.subtotal, 0) || 0) + 
    (cart.services?.reduce((sum, item) => sum + item.subtotal, 0) || 0);
    
  const cartCount = 
    (cart.products?.reduce((sum, item) => sum + item.quantity, 0) || 0) + 
    (cart.services?.reduce((sum, item) => sum + item.quantity, 0) || 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      loading,
      cartTotal,
      cartCount,
      addToCart, 
      addServiceToCart,
      updateCartItem, 
      removeFromCart,
      removeServiceFromCart,
      clearCart,
      refreshCart: fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
